import type { CSSProperties, ReactNode } from 'react';
import { PROSE_HEADING_ELEMENT_ID, PROSE_TEXT_ELEMENT_ID, QUOTE_BOUND_PROSE_PLACEHOLDER } from '@/blocks/domain/helpers/proseElements';
import { EditableFieldText } from '@/document-render/presentation/editing/EditableFieldText';
import { SelectableElement } from '@/document-render/presentation/editing/SelectableElement';
import { useProseCanvasHeading, useProseCanvasText } from './useProseCanvasElements';

/**
 * A prose block's two page elements in the BLOCK EDITOR (LLAMS-610 DB-05; BE-11, Figma `29:18`):
 * Heading and Text, selectable on the page like any field. Selecting Text mounts the editor slot
 * (`ProseEditorSlotProvider`) IN PLACE of the body; selecting Heading edits the raw title inline.
 * Rendered only under a block-editor `EditableDocumentProvider` on the editor surface — never on the
 * quote canvas, the template preview or the PDF. Without a slot (read-only canvas) nothing is editable.
 */

const placeholderStyle: CSSProperties = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 4,
  background: 'var(--accent-orange-50, #FDEFE5)',
  color: 'var(--accent-orange-700, #903C00)',
  fontSize: 12,
  fontWeight: 500
};

const emptyBodyStyle: CSSProperties = { color: 'var(--text-placeholder, #525B74)', fontStyle: 'italic' };

export function ProseCanvasHeading({ title, labelStyle, children }: { title: string; labelStyle: CSSProperties; children: ReactNode }) {
  const heading = useProseCanvasHeading(Boolean(children));
  if (heading.hidden) return null;

  return (
    <SelectableElement elementId={PROSE_HEADING_ELEMENT_ID} name="Heading" testId="document-render:selectable-element:prose-heading">
      {heading.isEditing ? (
        <div ref={heading.focusRef} style={labelStyle}>
          <EditableFieldText
            fieldId={PROSE_HEADING_ELEMENT_ID}
            value={title}
            onCommit={heading.commit}
            onFocusLabel="Edit Heading"
            testId="document-render:prose:heading-editable-text"
          />
        </div>
      ) : (
        children
      )}
    </SelectableElement>
  );
}

export interface ProseCanvasTextProps {
  kind: string;
  /** The RAW stored body the editor edits. */
  bodyHtml: string;
  /** The resolved, sliced body. */
  children: ReactNode;
}

/** `ProseRender` drops the later page slices of a body while its editor is open (or a placeholder stands in for it). */
export function ProseCanvasText({ kind, bodyHtml, children }: ProseCanvasTextProps) {
  const text = useProseCanvasText(kind, bodyHtml, Boolean(children));

  return (
    <SelectableElement elementId={PROSE_TEXT_ELEMENT_ID} name="Text" testId="document-render:selectable-element:prose-text">
      {text.content === 'placeholder' ? <span style={placeholderStyle}>{QUOTE_BOUND_PROSE_PLACEHOLDER}</span> : null}
      {text.content === 'editor' ? <div ref={text.focusRef}>{text.editor}</div> : null}
      {text.content === 'empty' ? <p style={emptyBodyStyle}>Add text</p> : null}
      {text.content === 'static' ? children : null}
    </SelectableElement>
  );
}
